import React from 'react';
import { formatCurrency } from '../../../utils/formatCurrency';
import { SkeletonRow } from '../../../components/ui/SkeletonRow';
import { BatchCheckbox } from '../../../components/BatchCheckbox';
import { InlineBatchActions } from '../../../components/InlineBatchActions';
import { useBatchSelection } from '../../../contexts/BatchSelectionContext';
import { FiCheck } from 'react-icons/fi';
import { Item } from '../../../hooks/useItems';

interface ItemTableProps {
  items: Item[];
  isLoading: boolean;
  onEdit: (item: Item) => void;
  onDelete: (itemId: string) => void;
}

const ItemTable: React.FC<ItemTableProps> = ({ items, isLoading, onEdit, onDelete }) => {
  const { 
    selectedItems, 
    selectAll, 
    deselectAll, 
    getSelectedCount, 
    isSelected,
    setOrderedIds 
  } = useBatchSelection();

  React.useEffect(() => {
    setOrderedIds(items.map(i => i.id));
  }, [items, setOrderedIds]);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white elev-1 dark:bg-neutral-900 dark:border-neutral-800 overflow-hidden min-w-full">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full">
            <tbody>
              <tr><td><div className="h-10 rounded-lg bg-gray-100 animate-pulse dark:bg-neutral-800" /></td></tr>
              <tr><td><div className="h-10 rounded-lg bg-gray-100 animate-pulse dark:bg-neutral-800" /></td></tr>
              <tr><td><div className="h-10 rounded-lg bg-gray-100 animate-pulse dark:bg-neutral-800" /></td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white elev-1 dark:bg-neutral-900 dark:border-neutral-800 p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">No items found</p>
      </div>
    );
  }

  const handleSelectAll = () => {
    if (getSelectedCount() === items.length) {
      deselectAll();
    } else {
      selectAll(items.map(item => item.id));
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white elev-1 dark:bg-neutral-900 dark:border-neutral-800 overflow-hidden min-w-full" data-testid="item-table">
      {/* Selection indicator */}
      {getSelectedCount() > 0 && (
        <InlineBatchActions entityType="items" />
      )}
      
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
          <thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur dark:bg-neutral-800/80">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 rounded cursor-pointer flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={handleSelectAll}
                    role="button"
                    tabIndex={0}
                  >
                    {getSelectedCount() === items.length && items.length > 0 && (
                      <FiCheck className="w-3 h-3 text-gray-700 dark:text-gray-300" />
                    )}
                  </div>
                </div>
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Name
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Category
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Rate
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Taxable
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {items.map((item) => (
              <tr key={item.id} className="odd:bg-white even:bg-gray-50/40 hover:bg-sedra/5 dark:odd:bg-neutral-900 dark:even:bg-neutral-900/60" data-testid="item-row">
                <td className="px-4 py-3 align-middle text-sm text-gray-700 dark:text-gray-200">
                  <BatchCheckbox id={item.id} />
                </td>
                <td className="px-4 py-3 align-middle text-sm text-gray-700 dark:text-gray-200">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.name}
                  </div>
                  {item.description && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                      {item.description}
                    </div>
                  )}
                  {item.use_customer_rate && (
                    <div className="mt-1">
                      <span className="ml-2 inline-flex items-center rounded-lg bg-sedra/10 px-2 py-0.5 text-xs font-semibold text-sedra dark:bg-sedra/20">
                        Customer Pricing
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-middle text-sm text-gray-700 dark:text-gray-200">
                  {item.category_name ? (
                    <span 
                      className={
                        `inline-flex px-2 py-1 text-xs font-semibold rounded-full ` +
                        (item.category_color 
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                          : 'bg-gray-50 text-gray-800 dark:bg-neutral-700 dark:text-neutral-200')
                      }
                    >
                      {item.category_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
                <td className="px-4 py-3 align-middle text-sm text-gray-700 dark:text-gray-200">
                  {item.rate ? formatCurrency(Number(item.rate)) : '$0.00'}
                </td>
                <td className="px-4 py-3 align-middle text-sm text-gray-700 dark:text-gray-200">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    Boolean(item.is_taxable)
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    {Boolean(item.is_taxable) ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-3 align-middle text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`Edit ${item.name}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(item.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                      aria-label={`Delete ${item.name}`}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ItemTable; 